import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    organizationSendingDomainContract,
    sendingDomainContract,
} from "@common/contracts/sending-domain.contract";
import type {
    CreateSendingEmailRequest,
    SendingEmailResponse,
    UpdateSendingEmailRequest,
} from "@common/schemas/sending-email.schema";
import { SendingDomainStatus } from "@common/types/domains/emailSending";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useSendingDomains } from "./sendingDomain.api";
import {
    getSendingOwnerQueryKey,
    requireOwner,
    type SendingOwner,
} from "./sendingOwner";

const workspaceClient = getBackendAPIClient(sendingDomainContract);
const organizationClient = getBackendAPIClient(
    organizationSendingDomainContract,
);

export const sendingEmailQueryKeys = {
    all: () => ["sending-emails"] as const,
    owner: (owner: SendingOwner) =>
        [
            ...sendingEmailQueryKeys.all(),
            ...getSendingOwnerQueryKey(owner),
        ] as const,
};

function listAllEmails(owner: SendingOwner) {
    return "workspaceId" in owner
        ? workspaceClient.listAllEmails({
              params: { workspaceId: owner.workspaceId },
          })
        : organizationClient.listAllEmails({
              params: { organizationId: owner.organizationId },
          });
}

function createEmail({
    owner,
    domainId,
    body,
}: {
    owner: SendingOwner;
    domainId: string;
    body: CreateSendingEmailRequest;
}) {
    return "workspaceId" in owner
        ? workspaceClient.createEmail({
              params: { workspaceId: owner.workspaceId, domainId },
              body,
          })
        : organizationClient.createEmail({
              params: { organizationId: owner.organizationId, domainId },
              body,
          });
}

function updateEmail({
    owner,
    emailId,
    body,
}: {
    owner: SendingOwner;
    emailId: string;
    body: UpdateSendingEmailRequest;
}) {
    return "workspaceId" in owner
        ? workspaceClient.updateEmail({
              params: { workspaceId: owner.workspaceId, emailId },
              body,
          })
        : organizationClient.updateEmail({
              params: { organizationId: owner.organizationId, emailId },
              body,
          });
}

function deleteEmail(owner: SendingOwner, emailId: string) {
    return "workspaceId" in owner
        ? workspaceClient.deleteEmail({
              params: { workspaceId: owner.workspaceId, emailId },
          })
        : organizationClient.deleteEmail({
              params: { organizationId: owner.organizationId, emailId },
          });
}

export type SendingEmailWithDomain = SendingEmailResponse & {
    domain: string;
    // Verification lives on the parent sending domain, not the email row: an
    // address is sendable only once its domain reaches `verified`. Resolved
    // here so callers (e.g. the invoice From picker) can gate on a single flag.
    isVerified: boolean;
};

export function useSendingEmails(
    owner: SendingOwner | null,
    options?: { enabled?: boolean },
) {
    const isEnabled = owner !== null && (options?.enabled ?? true);
    const { data: domains, isLoading: isLoadingDomains } = useSendingDomains(
        owner,
        { enabled: isEnabled },
    );

    const { data: emails, isLoading: isLoadingEmails } = useQuery({
        queryKey: owner
            ? sendingEmailQueryKeys.owner(owner)
            : sendingEmailQueryKeys.all(),
        queryFn: async (): Promise<SendingEmailResponse[]> => {
            const result = await listAllEmails(requireOwner(owner));
            if (result.status !== 200) {
                throw new Error(
                    `Failed to load sending emails (status ${result.status})`,
                );
            }
            return result.body.emails;
        },
        enabled: isEnabled,
    });

    const isLoading = isLoadingDomains || isLoadingEmails;

    const data = useMemo<SendingEmailWithDomain[] | undefined>(() => {
        if (isLoading || !emails) {
            return undefined;
        }
        const domainById = new Map(
            (domains ?? []).map((domain) => [domain.id, domain]),
        );
        return emails.map((email) => {
            const domain = domainById.get(email.sendingDomainId);
            return {
                ...email,
                domain: domain?.domain ?? "",
                isVerified: domain?.status === SendingDomainStatus.verified,
            };
        });
    }, [emails, domains, isLoading]);

    return { data, isLoading };
}

export function useCreateSendingEmail(owner: SendingOwner | null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            domainId,
            body,
        }: {
            domainId: string;
            body: CreateSendingEmailRequest;
        }): Promise<SendingEmailResponse> => {
            const result = await createEmail({
                owner: requireOwner(owner),
                domainId,
                body,
            });
            if (result.status !== 201) {
                throw new Error(
                    `Failed to create sending email (status ${result.status})`,
                );
            }
            return result.body;
        },
        onSuccess: () => {
            if (owner) {
                queryClient.invalidateQueries({
                    queryKey: sendingEmailQueryKeys.owner(owner),
                });
            }
        },
    });
}

export function useUpdateSendingEmail(owner: SendingOwner | null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            emailId,
            body,
        }: {
            emailId: string;
            body: UpdateSendingEmailRequest;
        }): Promise<SendingEmailResponse> => {
            const result = await updateEmail({
                owner: requireOwner(owner),
                emailId,
                body,
            });
            if (result.status !== 200) {
                throw new Error(
                    `Failed to update sending email (status ${result.status})`,
                );
            }
            return result.body;
        },
        onSuccess: () => {
            if (owner) {
                queryClient.invalidateQueries({
                    queryKey: sendingEmailQueryKeys.owner(owner),
                });
            }
        },
    });
}

export function useDeleteSendingEmail(owner: SendingOwner | null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (emailId: string) => {
            await deleteEmail(requireOwner(owner), emailId);
        },
        onSuccess: () => {
            if (owner) {
                queryClient.invalidateQueries({
                    queryKey: sendingEmailQueryKeys.owner(owner),
                });
            }
        },
    });
}
